import { ExternalLink } from "lucide-react";
import { useT } from "../lib/LangContext";

/**
 * Build a Google Translate proxy URL.
 * Uses the modern `.translate.goog` host pattern (the old
 * `translate.google.com/translate?sl=&tl=&u=` URL was deprecated by Google in
 * 2022 and now returns a blank page).
 *
 * Example:
 *   buildGoogleTranslateUrl("https://24ora.com/aruba/news", "nl")
 *   → "https://24ora-com.translate.goog/aruba/news?_x_tr_sl=auto&_x_tr_tl=nl&_x_tr_hl=nl"
 *
 * The target language defaults to "en" so existing callers stay backwards
 * compatible. Pass the current UI lang to translate into the reader's language.
 */
export const buildGoogleTranslateUrl = (originalUrl, targetLang = "en") => {
  try {
    const u = new URL(originalUrl);
    const proxyHost = u.hostname.replace(/\./g, "-") + ".translate.goog";
    const params = new URLSearchParams(u.search);
    params.set("_x_tr_sl", "auto");
    params.set("_x_tr_tl", targetLang);
    params.set("_x_tr_hl", targetLang);
    const qs = params.toString();
    return `${u.protocol}//${proxyHost}${u.pathname}${qs ? `?${qs}` : ""}${u.hash}`;
  } catch (e) {
    // Malformed URL — fall back to opening the original directly.
    return originalUrl;
  }
};

export const GoogleTranslateLink = ({
  originalUrl,
  sourceName,
  size = "md",
  testIdPrefix = "gt",
}) => {
  const { t } = useT();
  const isLg = size === "lg";
  return (
    <div className="flex flex-col gap-1.5" data-testid={`${testIdPrefix}-wrap`}>
      <a
        href={originalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-1.5 text-[#0AADA0] hover:text-[#08988c] transition-colors w-fit ${
          isLg ? "text-[14px]" : "text-[13px]"
        }`}
        data-testid={`${testIdPrefix}-link`}
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
      >
        {t("read_full_article_cta")}
        <ExternalLink size={isLg ? 14 : 12} />
      </a>
      <span
        className="text-[10px] text-[#9CA3AF]"
        data-testid={`${testIdPrefix}-attr`}
      >
        {t("translated_via_google")} {sourceName}
      </span>
    </div>
  );
};
